import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import { PageHeader } from "@pgs-core/ui/components/page-header";
import { SectionTabs, type SectionTab } from "@pgs-core/ui/components/section-tabs";
import type { ClientOutletContext } from "../lib/outlet-context";

// The menu module's tabs. Each is an exact route match so the per-menu editor
// (/menu/:id) leaves them all unlit.
const MENU_TABS: SectionTab[] = [
  { label: "Menus", href: "/menu", end: true },
  { label: "Items", href: "/menu/items", end: true },
  { label: "QR codes", href: "/menu/qr", end: true },
];

/**
 * The menu module's page header: the shared sticky tab header (see
 * SectionTabs sticky bar) wired with the menu tabs. `action` (Create menu / Add
 * item) renders right-aligned in the bar (spec §4.2 ruling).
 */
export function MenuModuleHeader({ action }: { action?: React.ReactNode }) {
  return <SectionTabs sticky items={MENU_TABS} action={action} />;
}

/**
 * Gate wrapper for the menu module pages: renders children only when the
 * module is active, otherwise the Add-ons upsell pointer. Provides the
 * shared outlet context to children via a render prop.
 */
export function MenuModuleGuard({ title, description, children }: {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: (ctx: ClientOutletContext) => React.ReactNode;
}) {
  const ctx = useOutletContext<ClientOutletContext>();
  const moduleActive =
    Array.isArray(ctx.activeModules) && ctx.activeModules.includes("restaurant_menu");

  if (!moduleActive) {
    return (
      <div>
        <PageHeader title={title} description={description} />
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>The menu module isn't active for your site</AlertTitle>
          <AlertDescription>
            See what it includes on the <Link className="underline" to="/modules">Add-ons</Link> page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return children(ctx);
}
